import React, { FC, useContext } from 'react'
import { PaddedContentStyled, PageMainDescriptionTextStyled, PageTitleStyled } from '../../styles/shared'
import styled from 'styled-components'
import Button from '../common/button'
import { BlueDotIcon, DiamondIcon, HeaderDashedCircleIcon } from '../../assets/icons'
import { ScreenBreakpointSize } from '../../utils/enums'
import { getScreenBreakpointSize } from '../../utils/getScreenBreakpointSize'
import { THandleDownload } from '../../utils/handleDownload'
import AppDownloadsDataContext from '../../context/appDownloadsDataContext'
import { IGetDownloadDataReturnItem } from '../../utils/getDownloadData'
import SaleBanner from './saleBanner'
import Sale from '../../typings/Sale'
import { DemoDownloadItem } from '../../data/Downloads'

interface IProps {
  platformItems: IGetDownloadDataReturnItem[]
  handleDownload: THandleDownload
}

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  margin-top: var(--spacing-48);
  max-width: var(--page-title-max-width);
  position: relative;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    align-items: center;
    margin-left: var(--spacing-16);
    margin-right: var(--spacing-16);
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    align-items: center;
    margin-top: var(--spacing-12);
  }
`

const DiamondIconStyled = styled.img`
  position: absolute;
  left: 70%;
  top: -40%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    left: 8%;
    top: -39%;
  }
`

const TitleStyled = styled(PageTitleStyled)`
  margin-bottom: 0;
  text-align: center;
  width: 100%;

  /* More specific selector to override the existing rule */
  &:not(:first-child) {
    margin-top: 0;
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    font-size: var(--page-title-font-size-small-screens);
    margin-top: var(--spacing-6);
    max-width: 95%;
    align-self: flex-start;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    max-width: 89%;
  }
`

const CustomPageMainDescriptionTextStyled = styled(PageMainDescriptionTextStyled)`
  font-size: var(--font-size-18);
  margin-bottom: var(--spacing-24);
  max-width: 49%;
  text-align: center;

  &:not(:first-child) {
    margin-top: var(--spacing-16);
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    max-width: 90%;
    text-align: center;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    max-width: 70%;
    text-align: center;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Large,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge, -1)}) {
    max-width: 70%;
    text-align: center;
  }
`
const CustomButtonStyled = styled(Button)`
  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    align-self: stretch;
    width: 100%;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    width: var(--button-max-width);
  }
`
const HighlightedTextStyled = styled.span`
  font-weight: bold;
  border-bottom: 2px solid var(--corn);
`
const DashedCircleIconStyled = styled.img`
  &[data-name='icon-1'] {
    position: absolute;
    left: 0;
    top: 2%;

    @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
      left: 90%;
      top: 5%;
    }

    @media screen and (min-width: ${getScreenBreakpointSize(
        ScreenBreakpointSize.Medium,
      )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
      left: 98%;
      top: 5%;
    }
  }

  &[data-name='icon-2'] {
    position: absolute;
    right: 0;
    top: 62%;

    @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
      display: none;
    }
  }
`

const BlueDotIconStyled = styled.img`
  &[data-name='icon-1'] {
    position: absolute;
    left: 6%;
    top: -8%;

    @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
      left: 87%;
      top: 22%;
    }

    @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and  (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
      left: 91%;
      top: 22%;
    }
  }
}

&[data-name='icon-2'] {
  position: absolute;
  right: 4%;
  top: 91%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    display: none;
  }
}
`

const CTAContainerStyled = styled.div`
  display: flex;
  gap: var(--spacing-10);
`

const HomeHeader: FC<IProps> = ({ handleDownload, platformItems }) => {
  const { currentPlatformItemIndex } = useContext(AppDownloadsDataContext)

  return (
    <>
      {Sale.enabled ? <SaleBanner showCountdown={true} sale={Sale} /> : null}
      <PaddedContentStyled>
        <ContainerStyled>
          <DiamondIconStyled alt="Diamond Icon" aria-hidden="true" src={DiamondIcon} />
          <TitleStyled>Free your mind.</TitleStyled>
          <CustomPageMainDescriptionTextStyled>
            Standard Notes is a free, secure note-taking app with powerful{' '}
            <HighlightedTextStyled>end-to-end encryption</HighlightedTextStyled>, unparalleled privacy features, and
            seamless cross-platform syncing on unlimited devices.{' '}
          </CustomPageMainDescriptionTextStyled>
          <CTAContainerStyled>
            <CustomButtonStyled
              text={'Download for free'}
              handleClick={() => {
                const item = platformItems[currentPlatformItemIndex as number]
                handleDownload(item.link, `/plans?downloaded=${item.id}`, item.id)
              }}
            />
            <CustomButtonStyled
              text={'Try live demo'}
              handleClick={() => {
                const item = DemoDownloadItem
                handleDownload(item.link, `/plans?downloaded=${item.id}`, item.id)
              }}
            />
          </CTAContainerStyled>
          <DashedCircleIconStyled
            data-name={'icon-1'}
            alt={'Dashed Circle Icon'}
            aria-hidden={'true'}
            width={32}
            height={32}
            src={HeaderDashedCircleIcon}
          />
          <BlueDotIconStyled data-name={'icon-1'} alt={'Blue Dot Icon'} aria-hidden={'true'} src={BlueDotIcon} />
          <DashedCircleIconStyled
            data-name={'icon-2'}
            alt={'Dashed Circle Icon'}
            aria-hidden={'true'}
            width={32}
            height={32}
            src={HeaderDashedCircleIcon}
          />
          <BlueDotIconStyled data-name={'icon-2'} alt={'Blue Dot Icon'} aria-hidden={'true'} src={BlueDotIcon} />
        </ContainerStyled>
      </PaddedContentStyled>
    </>
  )
}

export default HomeHeader
